/**
 * Lo que comparten la lista de reservas, la ficha y el alta.
 *
 * Vive fuera de los tres porque una cita se lee igual en la tabla que en su
 * ficha: si el estado se tradujera en dos sitios, acabarían diciendo cosas
 * distintas de la misma fila.
 *
 * La regla que manda en todo el archivo: las horas se pintan SIEMPRE en la zona
 * en que se reservó, que viaja en la propia cita. Un `toLocaleString` sin
 * `timeZone` usa la del navegador, y entonces el dueño de una estética en
 * Hermosillo que abre el panel desde Madrid ve sus citas nueve horas movidas.
 */
import { IconCalendarCheck, IconCheck, IconClock, IconUserX, IconX } from '@tabler/icons-react'
import type { Translate } from '@webbuilder/i18n'
import type { Booking } from '../../../lib/api'
import { activeLanguage, translate, translateServerError } from '../../../lib/i18n'
import { longDateWithWeekday } from '../../../lib/dates'

/* Guarda la CLAVE, no el texto: es una constante de módulo y no puede llamar a
   un hook. Quien la usa la pasa por su propio `t`. */
export const BOOKING_STATUS_KEY: Record<Booking['status'], string> = {
  held: 'bookings.status.held',
  confirmed: 'bookings.status.confirmed',
  cancelled: 'bookings.status.cancelled',
  completed: 'bookings.status.completed',
  no_show: 'bookings.status.no_show',
}

/**
 * El estado como clase CSS.
 *
 * La lectura cromática es la misma en tabla, calendario y ficha: apartada en
 * amarillo, confirmada en azul, asistió en verde y los dos desenlaces
 * problemáticos —cancelada/no asistió— en rojo.
 */
export const BOOKING_STATUS_TONE: Record<Booking['status'], string> = {
  held: 'is-pending',
  confirmed: 'is-confirmed',
  cancelled: 'is-danger',
  completed: 'is-completed',
  no_show: 'is-danger',
}

/** `10:00`, en la zona en que se reservó. */
export function timeIn(iso: string, timeZone: string): string {
  /* Reloj de 24 h en los dos idiomas: la columna de horas de una agenda no
     puede cambiar de ancho al cambiar de idioma. Ver `dashboardListDate`. */
  return new Intl.DateTimeFormat(activeLanguage(), {
    timeZone,
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
}

/** `mar 18 ago`, para la celda de la tabla. */
export function shortDateIn(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat(activeLanguage(), {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(new Date(iso))
}

/** `martes, 18 de agosto de 2026`, para la cabecera de la ficha. */
export function longDateIn(iso: string, timeZone: string): string {
  /* Por el formateador de la casa: `Intl` da todo en minúsculas y el panel lo
     arreglaba con `text-transform: capitalize`, que también ponía «De Agosto De
     2026». Los conectores en mayúscula no son estilo, son una falta. */
  return longDateWithWeekday(iso, timeZone)
}

/**
 * Cuántos días dura una reserva por días (de medianoche a medianoche en su
 * zona), o 0 si es por horas. Se deduce de la propia cita: la lista no tiene
 * el servicio a mano y una cita no cambia de tipo.
 */
export function allDayCount(booking: Pick<Booking, 'startsAt' | 'endsAt' | 'timezone'>): number {
  const span = Date.parse(booking.endsAt) - Date.parse(booking.startsAt)
  const days = Math.round(span / 86_400_000)
  if (days < 1 || Math.abs(span - days * 86_400_000) > 3_600_000) return 0
  return timeIn(booking.startsAt, booking.timezone) === '00:00' ? days : 0
}

/** La hora para la columna «Hora»: o «Todo el día» en una reserva por días. */
export function startTimeLabel(booking: Pick<Booking, 'startsAt' | 'endsAt' | 'timezone'>, t: Translate): string {
  return allDayCount(booking) ? t('bookings.allDay') : timeIn(booking.startsAt, booking.timezone)
}

/** Cuánto dura, en palabras: `1 h`, `45 min`, `1 h 30 min`. */
export function durationLabel(booking: Booking, t: Translate): string {
  const days = allDayCount(booking)
  if (days) return days === 1 ? t('bookings.dur.oneDay') : t('bookings.dur.days', { days })
  const minutes = Math.round((Date.parse(booking.endsAt) - Date.parse(booking.startsAt)) / 60_000)
  if (minutes < 60) return t('bookings.dur.minutes', { minutes })
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0
    ? t('bookings.dur.hours', { hours })
    : t('bookings.dur.hoursMinutes', { hours, minutes: rest })
}

/** Cuánto dura, sin abreviar, para fichas y diálogos con espacio. */
export function longDurationLabel(booking: Booking, t: Translate): string {
  const days = allDayCount(booking)
  if (days) return days === 1 ? t('bookings.dur.oneDay') : t('bookings.dur.days', { days })
  const minutes = Math.round((Date.parse(booking.endsAt) - Date.parse(booking.startsAt)) / 60_000)
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  const parts: string[] = []

  if (hours) parts.push(hours === 1 ? t('bookings.dur.longHour') : t('bookings.dur.longHours', { hours }))
  if (rest) parts.push(rest === 1 ? t('bookings.dur.longMinute') : t('bookings.dur.longMinutes', { minutes: rest }))

  return parts.join(' ') || t('bookings.dur.zero')
}

/** Ya pasó. La ficha lo usa para no ofrecer «cancelar» sobre algo de ayer. */
export function isPast(booking: Booking): boolean {
  return Date.parse(booking.endsAt) < Date.now()
}

/**
 * Pasó y nadie dijo cómo acabó.
 *
 * No es lo mismo que `isPast`: una cita de ayer marcada «Asistió» está cerrada y
 * no hay nada que hacer con ella. Lo que hay que ver desde lejos —en el mes, en
 * la lista, en el historial de un cliente— son las que siguen en «Apartada» o
 * «Confirmada» con la hora ya cumplida, porque cada una es un resultado que
 * falta por registrar y, si nadie lo registra, la agenda deja de decir la verdad.
 *
 * Toma lo mínimo y no un `Booking` entero: el historial de un contacto trae una
 * forma más corta de la misma cita y mide lo mismo.
 */
export function needsOutcome(booking: { endsAt: string; status: string }): boolean {
  if (booking.status !== 'held' && booking.status !== 'confirmed') return false
  return Date.parse(booking.endsAt) < Date.now()
}

/** Lo que dice la marca ámbar, igual en todas partes. */
export const OVERDUE_HINT_KEY = 'bookings.overdueHint'

/**
 * El mensaje de un fallo, sin el `500:` delante que no dice nada.
 *
 * Por `translateServerError` como el resto: con un `code` en la respuesta se
 * dice en el idioma del panel, y si no lo hay se cae a lo que mandó el servidor.
 * Leerlo a mano, como se hacía aquí, dejaba fuera cualquier traducción.
 */
export function bookingError(error: unknown): string {
  return translateServerError(error, translate('error.unexpected'))
}

/**
 * Una duración en minutos, escrita como la diría alguien.
 *
 * Vivía dentro de la lista de servicios y ahora la usan también la ficha y sus
 * atajos: tres sitios escribiendo «1 h 30 min» a mano acaban escribiéndolo de
 * tres maneras.
 */
export function minutesLabel(minutes: number | null): string {
  if (!minutes) return '—'
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`
}

/**
 * Individual o grupal, leído del CUPO del servicio.
 *
 * Es el cupo y no la cantidad de gente apuntada: una clase de ocho a la que
 * todavía no se ha apuntado nadie sigue siendo una clase, y llamarla
 * «individual» hasta que llegue el segundo diría que cambió de naturaleza al
 * llenarse. Es la misma regla que usa la ficha de una cita para no llamar
 * «Individual» al primero de una clase de ocho.
 *
 * Sin duración no hay calendario, así que tampoco hay modalidad de la que
 * hablar: se calla, igual que la celda de duración.
 */
export function modeOf(booking: { capacity: number; durationMinutes: number | null }, t: Translate): {
  group: boolean
  label: string
  title: string
} {
  if (booking.durationMinutes === null) return { group: false, label: '—', title: t('bookings.mode.none') }
  const capacity = booking.capacity || 1
  if (capacity <= 1) return { group: false, label: t('bookings.mode.single'), title: t('bookings.mode.singleTitle') }
  /* El cupo va en la etiqueta, no sólo en el title: «Grupal» a secas obliga a
     abrir el servicio para saber si caben tres o treinta, que es justo lo que se
     está mirando cuando se recorre esta columna. */
  return {
    group: true,
    label: t('bookings.mode.group', { capacity }),
    title: t('bookings.mode.groupTitle', { capacity }),
  }
}

/**
 * Una entrada de las listas: o una cita suelta, o una clase con su gente.
 *
 * En la base cada asistente es una fila propia —cada uno tiene su nombre, su
 * correo y su estado—, y eso está bien: quien no vino a una clase de seis no
 * hace que las otras cinco no vinieran. Pero enseñarlo así convierte una clase
 * de seis en seis renglones idénticos salvo el nombre, y el calendario en un día
 * lleno de la misma hora repetida.
 *
 * Aquí se juntan las que comparten servicio y horario. La clase es UNA entrada,
 * con su título y su cuenta; dentro sigue habiendo seis personas con su estado.
 */
export type BookingEntry =
  | { kind: 'one'; key: string; booking: Booking }
  | { kind: 'group'; key: string; lead: Booking; members: Booking[] }

export function groupBookings(bookings: Booking[]): BookingEntry[] {
  const buckets = new Map<string, Booking[]>()
  /* El orden de llegada manda: la lista ya viene ordenada por quien la pinta
     —por fecha, por nombre, por lo que se haya pulsado— y reordenar aquí
     desharía esa elección. Cada grupo aparece donde apareció su primer miembro. */
  const order: string[] = []
  for (const booking of bookings) {
    const key = `${booking.offerId ?? 'sin-servicio'}|${booking.startsAt}|${booking.endsAt}`
    const found = buckets.get(key)
    if (found) found.push(booking)
    else { buckets.set(key, [booking]); order.push(key) }
  }
  return order.map((key) => {
    const members = buckets.get(key) ?? []
    const lead = members[0] as Booking
    /* Uno solo no es una clase aunque el servicio tenga cupo para diez: se pinta
       como siempre, con su nombre por delante. */
    if (members.length < 2) return { kind: 'one' as const, key, booking: lead }
    return {
      kind: 'group' as const,
      key,
      lead,
      /* Por asiento, que es el orden en que se apuntaron. Por nombre, la lista se
         reordenaría sola en cuanto alguien corrigiera una errata. */
      members: [...members].sort((a, b) => a.seatIndex - b.seatIndex),
    }
  })
}

/** «3 personas». Con el número por delante, que es lo que se busca. */
export function peopleLabel(count: number, t: Translate): string {
  return count === 1 ? t('bookings.people.one') : t('bookings.people.many', { count })
}

/**
 * El icono de cada estado.
 *
 * Se separan «confirmada» y «asistió» a propósito, que es donde una palomita
 * para las dos confundiría: una cita confirmada está agendada y todavía no ha
 * ocurrido; una completada ya pasó y la persona vino. El calendario del mes
 * enseña las dos a la vez, así que tienen que distinguirse de un vistazo.
 */
export const BOOKING_STATUS_ICON: Record<Booking['status'], typeof IconCheck> = {
  /* Apartada es un carrito a medio pagar: un reloj, que es lo que está pasando. */
  held: IconClock,
  confirmed: IconCalendarCheck,
  completed: IconCheck,
  cancelled: IconX,
  /* No presentarse no es lo mismo que cancelar —una avisó y la otra no—, así que
     tampoco lleva el mismo icono. */
  no_show: IconUserX,
}

/**
 * Un teléfono, escrito para leerse.
 *
 * Llega tal y como lo tecleó quien reservó —«6191234566», «+1 619 123 45 66»,
 * con guiones o sin nada— y una tira de diez dígitos seguidos no se lee: hay
 * que contarlos con el dedo para saber si el prefijo es el que uno espera.
 * Agrupado se reconoce de un vistazo.
 *
 * La regla de oro: si no se entiende, NO se toca. Un número raro —una extensión,
 * un formato de otro país que no encaja en ningún grupo— se devuelve tal cual.
 * Inventarle un formato a lo que no lo tiene es peor que dejarlo feo: cambia lo
 * que la persona escribió y deja de poder compararse con lo que ella dio.
 *
 * Sólo para ENSEÑAR. El enlace `tel:` sigue llevando lo que hay en la base: el
 * marcador del teléfono quiere dígitos, no paréntesis.
 */
export function prettyPhone(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return trimmed
  /* Una extensión no se agrupa: lo que va después no es parte del número. */
  if (/[a-zA-Z]/.test(trimmed)) return trimmed

  const plus = trimmed.startsWith('+')
  const digits = trimmed.replace(/\D/g, '')
  if (digits.length < 7) return trimmed

  const group = (national: string, prefix: string): string => {
    if (national.length === 10) return `${prefix}(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`
    /* Sin diez dígitos no hay prefijo de zona que aislar, así que se parte en
       bloques legibles en vez de fingir una estructura que no se conoce. */
    if (national.length === 7) return `${prefix}${national.slice(0, 3)}-${national.slice(3)}`
    if (national.length === 8) return `${prefix}${national.slice(0, 4)} ${national.slice(4)}`
    if (national.length === 9) return `${prefix}${national.slice(0, 3)} ${national.slice(3, 6)} ${national.slice(6)}`
    return ''
  }

  /* Norteamérica marca el 1 delante y sus diez dígitos son los de siempre. */
  if (digits.length === 11 && digits.startsWith('1')) {
    const said = group(digits.slice(1), '+1 ')
    if (said) return said
  }
  if (!plus) {
    const said = group(digits, '')
    if (said) return said
  }
  /*
   * Con prefijo internacional, el corte lo marca quien escribió.
   *
   * Dónde acaba el país no se puede deducir del número: España son nueve
   * dígitos detrás del 34 y Estados Unidos diez detrás del 1, así que dar por
   * hecho «los diez últimos son el número» convertía +34 612345678 en
   * «+3 (461) 234-5678» —el prefijo partido por la mitad—. Si quien lo escribió
   * separó el país con un espacio o un guion, ahí está la respuesta; si lo pegó
   * todo, no hay forma de saberlo y se deja como está.
   */
  const separado = trimmed.match(/^\+(\d{1,3})[\s.-]+(.+)$/)
  const pais = separado?.[1]
  const resto = separado?.[2]
  if (pais && resto) {
    const said = group(resto.replace(/\D/g, ''), `+${pais} `)
    if (said) return said
  }
  return trimmed
}
